/**
 * GuestNameModal - Asks for the client's name and/or phone before taking a ticket.
 *
 * Two modes:
 *  - Default (no initialName): asks name + phone — used for guests without a name.
 *  - Phone-only (initialName provided): the name is already known, so it only asks
 *    for the phone. Used at the START of the booking flow for registered clients
 *    who have no phone yet (the server requires it to create the ticket), so they
 *    are not blocked at the very end after filling everything in.
 *
 * Either way the barber ends up with a real name and a way to reach the client.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { Phone, UserRound } from 'lucide-react';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui';
import toast from 'react-hot-toast';

interface GuestNameModalProps {
  isOpen: boolean;
  userId: string;
  onClose: () => void;
  onNameSaved: (name: string, phone: string) => void;
  /** If the name is already known, the modal only asks for the phone. */
  initialName?: string;
  initialPhone?: string;
}

const PHONE_REGEX = /^[+\d][\d\s-]{7,}$/;

export function GuestNameModal({
  isOpen,
  userId,
  onClose,
  onNameSaved,
  initialName = '',
  initialPhone = '',
}: GuestNameModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(initialPhone);
  const [saving, setSaving] = useState(false);

  // Name already known → phone-only mode (don't ask the user to retype their name).
  const nameKnown = initialName.trim().length >= 2;

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();

    if (!nameKnown) {
      if (!trimmedName) {
        toast.error(t('queue.guestName.errorEmpty', 'Por favor, ingresa tu nombre'));
        return;
      }
      if (trimmedName.length < 2) {
        toast.error(t('queue.guestName.errorTooShort', 'El nombre debe tener al menos 2 caracteres'));
        return;
      }
      if (trimmedName.length > 50) {
        toast.error(t('queue.guestName.errorTooLong', 'El nombre no puede exceder 50 caracteres'));
        return;
      }
    }

    if (!trimmedPhone) {
      toast.error(t('queue.guestName.errorPhoneEmpty', 'Por favor, ingresa tu teléfono'));
      return;
    }

    if (!PHONE_REGEX.test(trimmedPhone)) {
      toast.error(t('queue.guestName.errorPhoneInvalid', 'Introduce un teléfono válido'));
      return;
    }

    try {
      setSaving(true);

      // Only write the name when we actually asked for it.
      const update: Record<string, unknown> = {
        phoneNumber: trimmedPhone,
        updatedAt: Timestamp.now(),
      };
      if (!nameKnown) update.displayName = trimmedName;

      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, update);

      toast.success(t('queue.guestName.success', '¡Datos guardados!'));
      onNameSaved(nameKnown ? initialName.trim() : trimmedName, trimmedPhone);
    } catch (error) {
      console.error('Error saving guest data:', error);
      toast.error(t('queue.guestName.errorSaving', 'Error al guardar los datos'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-surface border border-hairline rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            {nameKnown ? (
              <Phone className="w-7 h-7 text-accent" aria-hidden="true" />
            ) : (
              <UserRound className="w-7 h-7 text-accent" aria-hidden="true" />
            )}
          </div>
          <h2 className="font-sans text-xl font-semibold text-ink">
            {nameKnown
              ? t('queue.guestPhone.title', 'Añade tu teléfono')
              : t('queue.guestName.title', '¿Cómo te llamas?')}
          </h2>
          <p className="text-sm text-ink-muted mt-2">
            {nameKnown
              ? t('queue.guestPhone.description', 'Lo necesitamos para avisarte cuando se acerque tu turno.')
              : t('queue.guestName.description', 'Tu nombre y teléfono serán visibles para el barbero')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!nameKnown && (
            <div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('queue.guestName.placeholder', 'Ej: Manuel, Ana, Carlos...')}
                className="w-full px-4 py-3 text-lg border-2 border-hairline rounded-xl
                           focus:border-accent focus:ring-2 focus:ring-focus
                           bg-surface-3 text-ink transition-all
                           placeholder:text-ink-subtle"
                autoFocus
                maxLength={50}
              />
              <p className="text-xs text-ink-subtle mt-1 text-right">{name.length}/50</p>
            </div>
          )}

          <div>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('queue.guestName.phonePlaceholder', 'Teléfono (ej: 612345678)')}
              className="w-full px-4 py-3 text-lg border-2 border-hairline rounded-xl
                         focus:border-accent focus:ring-2 focus:ring-focus
                         bg-surface-3 text-ink transition-all
                         placeholder:text-ink-subtle"
              autoComplete="tel"
              inputMode="tel"
              autoFocus={nameKnown}
              maxLength={20}
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
              disabled={saving}
            >
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              isLoading={saving}
              disabled={(!nameKnown && !name.trim()) || !phone.trim() || saving}
            >
              {t('queue.guestName.continue', 'Continuar')}
            </Button>
          </div>
        </form>

        {/* Info note */}
        <p className="text-xs text-center text-ink-subtle mt-4">
          {t('queue.guestName.note', 'Solo el barbero verá tus datos')}
        </p>
      </div>
    </div>
  );
}
